import type { WebSocketResponse } from "@/types/types";
import { isListGamesResponse, ListGames } from "@/types";
import { getUserIdCookie } from "@/utils/cookies";
import { BaseHandler } from "./base.handler";
import { WebSocketService } from "../websocket.service";

/** One page of a campaign's eligible game names, as the server searched and sorted them. */
export type GamesPage = {
  names: string[];
  /** How many names match the whole query (not just this page). */
  total: number;
  /** Where the next page starts, or `null` when this is the last page. */
  nextOffset: number | null;
};

type Pending = {
  resolve: (page: GamesPage) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

/** A request with no answer by then has been lost (dropped socket, server error). */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * `action:list_games` — a page of game names for the games dialog of an "All games" campaign,
 * whose names never ship on the campaigns stream (the selection can be the whole catalogue).
 * Request/response over the widget socket: each request carries a `request_id` the server echoes
 * back, so several pages can be in flight (scrolling, typing) and each lands on its own promise.
 */
export class GamesListHandler extends BaseHandler {
  private pending = new Map<string, Pending>();
  private seq = 0;

  canHandle(messageType: string): boolean {
    return messageType === ListGames;
  }

  async handle(message: WebSocketResponse): Promise<void> {
    if (!this.validateMessage(message) || !isListGamesResponse(message)) return;
    const id = message.request_id;
    const pending = id ? this.pending.get(id) : undefined;
    if (!id || !pending) return;
    clearTimeout(pending.timer);
    this.pending.delete(id);
    if (message.result === "ok") {
      pending.resolve({
        names: message.names ?? [],
        total: message.total ?? 0,
        nextOffset: message.next_offset ?? null,
      });
    } else {
      pending.reject(new Error(message.message ?? "list_games failed"));
    }
  }

  listGames(
    campaignId: number,
    {
      query = "",
      offset = 0,
      limit = 50,
    }: { query?: string; offset?: number; limit?: number }
  ): Promise<GamesPage> {
    const requestId = `games-${++this.seq}`;
    return new Promise((resolve, reject) => {
      const sent = WebSocketService.getInstance().send(ListGames, {
        player: getUserIdCookie(),
        campaign: campaignId,
        query,
        offset,
        limit,
        request_id: requestId,
      });
      if (!sent) {
        reject(new Error("not connected"));
        return;
      }
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error("list_games timed out"));
      }, REQUEST_TIMEOUT_MS);
      this.pending.set(requestId, { resolve, reject, timer });
    });
  }

  /** On disconnect: the answers to in-flight requests won't arrive — fail them now. */
  reset(): void {
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error("disconnected"));
      this.pending.delete(id);
    }
  }
}

export const gamesListHandler = new GamesListHandler();
